import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ROLES } from '../../constants/roles';
import creditService from '../../services/credit.service';
import Toast from '../../components/Toast/Toast';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';

const fmt = (a) => `₹${Number(a || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const fmtDt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const ReviewAdvancePayments = () => {
  const { user } = useAuth();
  const isSuperAdminRole = user?.role === ROLES.SUPER_ADMIN;

  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [acting, setActing] = useState(null);
  const [rejectModal, setRejectModal] = useState({ open: false, id: null });
  const [rejectReason, setRejectReason] = useState('');

  const fetchPending = async () => {
    setLoading(true);
    try {
      const cid = isSuperAdminRole ? localStorage.getItem('selectedCompanyId') : null;
      if (isSuperAdminRole && !cid) { setPending([]); setLoading(false); return; }
      const res = await creditService.getPendingAdvancePayments(cid);
      if (res.success) setPending(res.data || []);
    } catch {
      setToast({ message: 'Failed to fetch pending advance payments', type: 'error' });
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchPending(); }, [isSuperAdminRole]);

  const handleConfirm = async (id) => {
    setActing(id);
    try {
      const res = await creditService.confirmAdvancePayment(id);
      setToast({ message: 'Advance payment confirmed and credited!', type: 'success' });
      fetchPending();
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Failed to confirm', type: 'error' });
    } finally { setActing(null); }
  };

  const handleRejectSubmit = async () => {
    const id = rejectModal.id;
    setRejectModal({ open: false, id: null });
    setActing(id);
    try {
      await creditService.rejectAdvancePayment(id, rejectReason);
      setToast({ message: 'Advance payment rejected', type: 'success' });
      setRejectReason('');
      fetchPending();
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Failed to reject', type: 'error' });
    } finally { setActing(null); }
  };

  return (
    <div className="pay-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <ConfirmModal
        isOpen={rejectModal.open}
        title="Reject Advance Payment"
        message="This advance will be rejected and no credit will be added."
        confirmLabel="✕ Reject"
        confirmType="danger"
        onConfirm={handleRejectSubmit}
        onClose={() => { setRejectModal({ open: false, id: null }); setRejectReason(''); }}
      >
        <label style={{ fontSize: 12, fontWeight: 600, color: '#495057', display: 'block', marginBottom: 6 }}>
          Reason (optional)
        </label>
        <textarea rows={2} style={{ width: '100%', padding: '8px 12px', border: '1px solid #dee2e6', borderRadius: 8, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
          value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Why are you rejecting this?" />
      </ConfirmModal>

      <div className="pay-header">
        <h4 className="pay-title">Review Advance Payments</h4>
        <p className="pay-subtitle">Confirm or reject advance payments submitted by the team</p>
      </div>

      {isSuperAdminRole && !localStorage.getItem('selectedCompanyId') ? (
        <div className="pay-empty"><p>Select a company from the top bar to view pending advance payments.</p></div>
      ) : (
        <div className="pay-table-container">
          {loading ? <div className="pay-loading">Loading...</div>
          : pending.length === 0 ? <div className="pay-empty"><p>🎉 No advance payments waiting for review</p></div>
          : (
            <table className="pay-table">
              <thead>
                <tr>
                  <th>#</th><th>Type</th><th>Name</th><th>Amount</th><th>Mode</th>
                  <th>Date</th><th>Submitted By</th><th>Remark</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((ap, idx) => (
                  <tr key={ap.id}>
                    <td>{idx + 1}</td>
                    <td>
                      <span style={{
                        padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                        background: ap.ownerType === 'SUB_DEALER' ? '#fff3e0' : '#e8f5e9',
                        color: ap.ownerType === 'SUB_DEALER' ? '#e65100' : '#2e7d32',
                      }}>
                        {ap.ownerType === 'SUB_DEALER' ? 'SUB-DEALER' : 'DEALER'}
                      </span>
                    </td>
                    <td style={{ fontWeight: 600 }}>{ap.ownerName || '—'}</td>
                    <td style={{ fontWeight: 700 }}>{fmt(ap.amount)}</td>
                    <td style={{ fontSize: 12 }}>{ap.paymentMode || '—'}</td>
                    <td style={{ fontSize: 12 }}>{fmtDt(ap.paymentDate)}</td>
                    <td style={{ fontSize: 13 }}>
                      {ap.createdByFirstName ? `${ap.createdByFirstName} ${ap.createdByLastName}` : '—'}
                    </td>
                    <td style={{ maxWidth: 200, fontSize: 13, color: '#495057' }}>{ap.remark || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 5 }}>
                        <button onClick={() => handleConfirm(ap.id)} disabled={acting === ap.id}
                          style={{ padding: '4px 10px', background: '#4caf50', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                          {acting === ap.id ? '...' : '✓ Confirm'}
                        </button>
                        <button onClick={() => setRejectModal({ open: true, id: ap.id })} disabled={acting === ap.id}
                          style={{ padding: '4px 10px', background: '#fce4ec', color: '#c62828', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                          ✕ Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default ReviewAdvancePayments;